// ipad-pwa/src/lib/api.js
// Bearer-token API client. Pulls the device_token from the in-memory cache
// populated by lib/auth.js bootstrap(). In Phase-2 sealed mode it also
// attaches HMAC-SHA256 body signatures via X-Body-Signature; in legacy
// mode the header is omitted and the server's soft mode allows the
// request through.
//
// Every mutation still carries a client-generated `client_uuid` AND the
// same value as `Idempotency-Key` so the server dedupes retries —
// offline-queue replays land cleanly on the existing UNIQUE constraint.

import { getDeviceToken, getSigningSecretBytes } from './auth.js';
import { hmacBodySignature } from './crypto.js';

const BASE = '/api';
const enc = new TextEncoder();

async function request(path, { method = 'GET', body, idempotencyKey } = {}) {
  const headers = { Accept: 'application/json' };
  const token = getDeviceToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  // Compute the raw body bytes ONCE so we can HMAC-sign the exact same
  // sequence we transmit. Stringify here (server reads `req.rawBody` set
  // by its express.json() verify hook).
  let bodyBytes = null;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    bodyBytes = enc.encode(JSON.stringify(body));
  }

  // Sign mutations when we have a signing secret in memory. GET requests
  // can't be tampered with usefully (no payload to inject), so we skip
  // signing them.
  const signingKey = getSigningSecretBytes();
  if (signingKey && method !== 'GET') {
    headers['X-Body-Signature'] = await hmacBodySignature(
      signingKey,
      method,
      BASE + path,
      bodyBytes || new Uint8Array(0),
    );
  }

  const res = await fetch(BASE + path, { method, headers, body: bodyBytes });
  const text = await res.text();
  const data = text ? (() => { try { return JSON.parse(text); } catch { return null; } })() : null;
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  pair: (code) => request('/devices/pair', { method: 'POST', body: { code } }),

  signin: (body) =>
    request('/sessions/signin', { method: 'POST', body, idempotencyKey: body.client_uuid }),

  signout: (client_uuid, body) =>
    request(`/sessions/${client_uuid}/signout`, { method: 'POST', body, idempotencyKey: client_uuid }),

  editCathayId: (client_uuid, cathay_id) =>
    request(`/sessions/${client_uuid}/edit-cathay-id`, {
      method: 'POST',
      body: { cathay_id },
    }),

  forceClose: (sessionId) => request(`/sessions/${sessionId}/force-close`, { method: 'POST' }),

  active: () => request('/sessions/active'),

  health: () => request('/health'),

  // v2 — upload the CX Staff Card photo for a freshly-signed-in session.
  // Multipart (skips HMAC body-signing path; server route documents why).
  uploadStaffCard: async (client_uuid, file) => {
    const fd = new FormData();
    fd.append('staff_card', file, file.name || 'staff-card.jpg');
    const headers = { Accept: 'application/json' };
    const token = getDeviceToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}/sessions/${encodeURIComponent(client_uuid)}/staff-card`, {
      method: 'POST',
      headers,
      body: fd,
    });
    const text = await res.text();
    const data = text ? (() => { try { return JSON.parse(text); } catch { return null; } })() : null;
    if (!res.ok) {
      const err = new Error(data?.error || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  },
};
